import React, { useState, useEffect } from "react";
import Dashboard from "./components/Dashboard";
import Login from "./components/Login";
import ModalCrearUsuario from "./components/ModalCrearUsuario";
import ListaUsuarios from "./components/ListaUsuarios";
import Calendario from "./components/Calendario";
import Pendientes from "./components/Pendientes";
import Logs from "./components/Logs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  listarLicitaciones,
  listarPaises,
  listarUsuarios,
  autenticarUsuario,
  logout,
  crearUsuario,
  eliminarUsuario,
  actualizarUsuario,
} from "./api";
import {
  HiOutlineDocumentText,
  HiOutlineInbox,
  HiOutlineUserAdd,
  HiOutlineUsers,
  HiOutlineCalendar,
  HiOutlineLogout,
  HiOutlineSearch,
  HiOutlineCog,
  HiOutlineCurrencyEuro
} from "react-icons/hi";
import "./styles.css";

function App() {
  const [usuario, setUsuario] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroPais, setFiltroPais] = useState("");
  const [busquedaGeneral, setBusquedaGeneral] = useState("");
  const [paises, setPaises] = useState([]);
  const [usuariosLista, setUsuariosLista] = useState([]);
  const [modalCrearUsuarioVisible, setModalCrearUsuarioVisible] = useState(false);
  const [modalListaUsuariosVisible, setModalListaUsuariosVisible] = useState(false);
  const [vistaActiva, setVistaActiva] = useState("licitaciones"); // "licitaciones" | "pendientes" | "calendario"
  const [contadorPendientes, setContadorPendientes] = useState(0);
  const [dashboardKey, setDashboardKey] = useState(0);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [soloFavoritos, setSoloFavoritos] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("currentUser");
    if (stored) setUsuario(JSON.parse(stored));
  }, []);

  useEffect(() => {
    if (!usuario) return;
    async function fetchPaises() {
      try {
        const data = await listarPaises();
        setPaises(data);
      } catch (err) {
        console.error("Error cargando países:", err);
      }
    }
    fetchPaises();
  }, [usuario]);

  const fetchUsuarios = async () => {
    try {
      const data = await listarUsuarios();
      setUsuariosLista(data);
    } catch (err) {
      console.error("Error al listar usuarios:", err);
    }
  };

  const fetchContadorPendientes = async () => {
    if (usuario?.rol === "admin") {
      try {
        const data = await listarLicitaciones();
        const total = (data || []).filter(
          (l) => (l.estado || "").toLowerCase() === "pendiente de aceptar"
        ).length;
        setContadorPendientes(total);
      } catch (err) {
        console.error("Error al calcular contador de pendientes:", err);
      }
    }
  };

  useEffect(() => {
    if (usuario?.rol === "admin") {
      fetchUsuarios();
      fetchContadorPendientes();
    }
  }, [usuario, dashboardKey]);

  const handleLogin = async (username, password) => {
    try {
      await autenticarUsuario(username, password);
      // autenticarUsuario ya guardó el token en localStorage 'currentUser' vía api.js
      const stored = JSON.parse(localStorage.getItem("currentUser"));
      setUsuario(stored);
    } catch (err) {
      throw err;
    }
  };

  const handleLogout = () => {
    logout(); // api.js limpia 'currentUser'
    setUsuario(null);
    setModalCrearUsuarioVisible(false);
    setModalListaUsuariosVisible(false);
    setVistaActiva("licitaciones");
    setSidebarVisible(false);
  };

  const actualizarDashboard = () => {
    setDashboardKey(prev => prev + 1);
    if (usuario) {
      listarPaises()
        .then(setPaises)
        .catch(err => console.error("Error recargando países:", err));
    }
  };

  const handleCrearUsuario = async (username, password, rolNuevo) => {
    try {
      await crearUsuario({ username, password, rol: rolNuevo });
      setModalCrearUsuarioVisible(false);
      fetchUsuarios();
      actualizarDashboard();
      toast.success(`Usuario ${username} creado`);
    } catch (err) {
      console.error("Error al crear usuario:", err);
      toast.error(err.message || "Error al crear usuario");
    }
  };

  const handleEliminarUsuario = async (username) => {
    try {
      await eliminarUsuario(username);
      fetchUsuarios();
      actualizarDashboard();
      toast.success(`Usuario eliminado`);
    } catch (err) {
      toast.error(err.message || "Error eliminando usuario");
    }
  };

  const handleActualizarUsuario = async (username, password, rol) => {
    try {
      await actualizarUsuario({ username, password, rol });
      toast.success(`Usuario ${username} actualizado`);
      fetchUsuarios();
      actualizarDashboard();
    } catch (err) {
      toast.error("Error actualizando usuario");
    }
  };

  if (!usuario) return <Login onLogin={handleLogin} />;

  if (window.location.pathname === "/logs") {
    if (usuario?.rol !== "admin") return <div style={{ color: "white", padding: "2rem" }}>No tienes permisos para ver esta página.</div>;
    return <Logs onBack={() => window.location.href = "/"} />;
  }

  const esAdmin = usuario?.rol === "admin";

  return (
    <div className="app-container">
      <button
        className="btn-hamburguesa"
        onClick={() => setSidebarVisible(!sidebarVisible)}
      >
        {sidebarVisible ? "✕" : "☰"}
      </button>

      {sidebarVisible && (
        <div 
          className="sidebar-overlay" 
          onClick={() => setSidebarVisible(false)}
        />
      )}

      <div className={`sidebar ${sidebarVisible ? "visible" : ""}`}>
        <div className="sidebar-brand">
          <div className="brand-logo">V</div>
          <span>GESTOR</span>
        </div>

        {esAdmin && (
          <nav className="sidebar-nav">
            <h2>Gestión</h2>
            <button
              className={vistaActiva === "licitaciones" ? "active" : ""}
              onClick={() => {
                setVistaActiva("licitaciones");
                setSidebarVisible(false);
              }}
            >
              <HiOutlineDocumentText size={20} />
              Licitaciones
            </button>

            <button
              className={`btn-sidebar-pendientes ${vistaActiva === "pendientes" ? "active" : ""}`}
              onClick={() => {
                setVistaActiva("pendientes");
                setSidebarVisible(false);
              }}
            >
              <HiOutlineInbox size={20} />
              <span className="sidebar-btn-label">Pendientes</span>
              {contadorPendientes > 0 && (
                <span className="badge-sidebar-pendientes">
                  {contadorPendientes}
                </span>
              )}
            </button>

            <button
              onClick={() => {
                setModalCrearUsuarioVisible(true);
                setSidebarVisible(false);
              }}
            >
              <HiOutlineUserAdd size={20} />
              Crear Usuario
            </button>

            <button
              onClick={() => {
                setModalListaUsuariosVisible(true);
                setSidebarVisible(false);
              }}
            >
              <HiOutlineUsers size={20} />
              Ver Usuarios
            </button>

            <button
              className={vistaActiva === "calendario" ? "active" : ""}
              onClick={() => {
                setVistaActiva("calendario");
                setSidebarVisible(false);
              }}
            >
              <HiOutlineCalendar size={20} />
              Calendario
            </button>
          </nav>
        )}

        <nav className="sidebar-nav">
          <h2>Filtros</h2>
          <div className="filter-group">
            <div className="search-input-wrapper">
              <HiOutlineSearch className="search-icon" />
              <input
                type="text"
                placeholder="Buscar..."
                value={busquedaGeneral}
                onChange={(e) => setBusquedaGeneral(e.target.value)}
              />
            </div>
          </div>

          <div className="filter-group">
            <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
              <option value="">Todos los estados</option>
              <option value="pendiente">En curso</option>
              <option value="cierra hoy">Cierra hoy</option>
              <option value="presentada">Presentada</option>
              <option value="proxima">Próxima</option>
              <option value="cerrada">Cerrada</option>
              <option value="adjudicada">Adjudicada</option>
              <option value="no adjudicada">No adjudicada</option>
            </select>
          </div>

          <div className="filter-group">
            <select value={filtroPais} onChange={(e) => setFiltroPais(e.target.value)}>
              <option value="">Todos los países</option>
              {paises.map((pais, idx) => (
                <option key={idx} value={pais}>
                  {pais}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group filter-checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={soloFavoritos}
                onChange={(e) => setSoloFavoritos(e.target.checked)}
              />
              <span>Mostrar solo favoritos</span>
            </label>
          </div>
        </nav>

        <div className="sidebar-separator" />

        <div className="sidebar-portals-section">
          <h2>Portales</h2>
          <div className="sidebar-portals-grid">
            <a 
              href="https://mantenimientos.vipeca.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="portal-card"
              title="Mantenimientos"
            >
              <HiOutlineCog size={22} />
              <span>Mantenimientos</span>
            </a>
            <a 
              href="https://subvenciones.vipeca.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="portal-card"
              title="Subvenciones"
            >
              <HiOutlineCurrencyEuro size={22} />
              <span>Subvenciones</span>
            </a>
          </div>
        </div>


        <button
          onClick={handleLogout}
          className="btn-logout"
        >
          <HiOutlineLogout size={20} />
          Cerrar sesión
        </button>
      </div>

      <div className="main-content">
        {vistaActiva === "calendario" ? (
          <Calendario onCerrar={() => setVistaActiva("licitaciones")} />
        ) : vistaActiva === "pendientes" && esAdmin ? (
          <Pendientes
            usuario={usuario}
            onActualizarGlobal={actualizarDashboard}
            triggerActualizar={dashboardKey}
            paises={paises}
          />
        ) : (
          <Dashboard
            filtroEstado={filtroEstado}
            filtroPais={filtroPais}
            busquedaGeneral={busquedaGeneral}
            usuario={usuario}
            onActualizarGlobal={actualizarDashboard}
            triggerActualizar={dashboardKey}
            soloFavoritos={soloFavoritos}
          />
        )}
      </div>

      {modalCrearUsuarioVisible && (
        <ModalCrearUsuario
          visible={modalCrearUsuarioVisible}
          onClose={() => setModalCrearUsuarioVisible(false)}
          onGuardar={handleCrearUsuario}
        />
      )}

      {modalListaUsuariosVisible && (
        <ListaUsuarios
          visible={modalListaUsuariosVisible}
          onClose={() => setModalListaUsuariosVisible(false)}
          usuarios={usuariosLista}
          onEliminar={handleEliminarUsuario}
          onActualizar={handleActualizarUsuario}
        />
      )}

      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
}

export default App;
